/**
 * TimelineItem — unified vertical timeline entry.
 *
 * Covers three display modes depending on the combination of props:
 *
 *  ① Default  (dotColor + title/subtitle/meta + optional tooltip)
 *             — for event logs & activity feeds (e.g. AlertTimelineItem)
 *
 *  ② Card     (accent Tailwind class + label + value datetime)
 *             — for device/connection event rows (replaces DeviceTimelineRow)
 *             Enabled by `card` prop.
 *
 *  ③ Step     (done/current/connectorColor + pulseColor)
 *             — for multi-step action-log timelines (replaces inline JSX in ActionCard)
 *             Enabled when `done` prop is provided.
 *
 * Usage – Default:
 * ```tsx
 * <TimelineItem dotColor="#10b981" icon={<LuCheck />} title="CREATE-SYNC"
 *   subtitle="Alert dispatched" meta="200 | 9 May 2026" tooltip="OK" />
 * ```
 * Usage – Card:
 * ```tsx
 * <TimelineItem card accent="bg-emerald-500" icon={<LuWifi />}
 *   label="Last Connected" value={item.lastConnectedDtm} />
 * <TimelineItem card accent="bg-amber-500" icon={<LuClock />}
 *   label="Last Failed HB" value={item.lastFailedHBDtm} last />
 * ```
 * Usage – Step:
 * ```tsx
 * <TimelineItem dotColor={step.color} icon={step.icon} title={step.label}
 *   value={step.time} done={step.done} current={i === currentIdx}
 *   connectorColor={step.done && steps[i+1]?.done ? step.color : '#cbd5e1'}
 *   pulseColor={step.color} last={isLast} />
 * ```
 */
import React from 'react';
import classNames from 'classnames';
import Tooltip from '@/components/atoms/Tooltip';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { Text } from '../atoms/AppText';

// ── Pulse animation (injected once into <head> for step mode) ─────────────────
const PULSE_CSS = `
@keyframes tl-border-pulse {
    0%, 100% { box-shadow: 0 0 0 0px var(--tl-pulse-color, rgba(99,102,241,0.25)), 0 0 0 3px var(--tl-glow-color, rgba(99,102,241,0.08)); }
    50%       { box-shadow: 0 0 0 4px var(--tl-pulse-color, rgba(99,102,241,0.1)),  0 0 0 7px var(--tl-glow-color, rgba(99,102,241,0.03)); }
}
.tl-dot-current { animation: tl-border-pulse 1.8s ease-in-out infinite; }
`;
let _pulseCssInjected = false;
function ensurePulseCss() {
  if (_pulseCssInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.textContent = PULSE_CSS;
  document.head.appendChild(el);
  _pulseCssInjected = true;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TimelineItemProps {
  // ── Dot
  /** Icon rendered inside the dot (should be ~w-3 h-3 text-white) */
  icon: React.ReactNode;
  /** CSS color for the dot background — used in Default & Step modes */
  dotColor?: string;
  /** Tailwind BG class for the dot (e.g. "bg-emerald-500") — used in Card mode */
  accent?: string;

  // ── Content
  /** Primary bold label / title */
  title?: string;
  /** Alias for title — kept for DeviceTimelineRow backward compat */
  label?: string;
  /** Secondary muted line (Default mode) */
  subtitle?: string;
  /** Smallest meta/timestamp line (Default mode) */
  meta?: string;
  /** ISO datetime string — auto-formatted as full date + relative ago (Card & Step modes) */
  value?: string | null;

  // ── Step state
  /** Whether this step has been completed/reached */
  done?: boolean;
  /** Whether this is the current active step — shows pulse animation */
  current?: boolean;
  /** CSS color of the connector line drawn below this dot */
  connectorColor?: string;
  /** CSS color used in the pulse glow when current=true (defaults to dotColor) */
  pulseColor?: string;

  // ── Layout
  /** Hide the connector line — use on the last item in a list */
  last?: boolean;
  /** Render content in a bordered card (Card mode) */
  card?: boolean;
  /** Tooltip text shown on hover (Default mode) */
  tooltip?: string;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const TimelineItem = ({
  icon,
  dotColor,
  accent,
  title,
  label,
  subtitle,
  meta,
  value,
  done,
  current = false,
  connectorColor = '#cbd5e1',
  pulseColor,
  last = false,
  card = false,
  tooltip,
  className = '',
}: TimelineItemProps) => {
  const effectiveTitle = title ?? label ?? '';
  const isStepMode = done !== undefined;

  // ── ① Card mode ─────────────────────────────────────────────────────────
  if (card) {
    return (
      <div className={classNames('flex gap-3', className)}>
        {/* Dot + connector */}
        <div className="flex flex-col items-center">
          <div
            className={classNames(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full shadow-sm',
              accent,
            )}
          >
            {icon}
          </div>
          {!last && (
            <div
              className="w-px flex-1 mt-0.5 min-h-[20px]"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(150,150,150,0.25), transparent)',
              }}
            />
          )}
        </div>
        {/* Content card */}
        <div
          className={classNames(
            'flex-1 min-w-0 rounded-xl border p-3 mb-3',
            APP_BORDER.soft,
            APP_BG.surface,
          )}
        >
          <Text
            as="p"
            size="xs"
            color="muted"
            className="uppercase tracking-widest font-semibold mb-0.5"
          >
            {effectiveTitle}
          </Text>
          {value ? (
            <div className="flex flex-col">
              <Text as="p" size="sm" weight="semibold" color="default">
                {getDateTime(value)}
              </Text>
              <Text as="p" size="xs" color="subtle">
                {getDateTimeAgo(value)}
              </Text>
            </div>
          ) : (
            <Text as="p" size="sm" color="muted">
              Never recorded
            </Text>
          )}
        </div>
      </div>
    );
  }

  // ── ② Step mode ─────────────────────────────────────────────────────────
  if (isStepMode) {
    ensurePulseCss();
    const gl = pulseColor ?? dotColor ?? '#6366f1';
    const dotStyle: React.CSSProperties = {
      background: done ? dotColor : 'transparent',
      border: done ? 'none' : '2px dashed #cbd5e1',
      boxShadow: done ? `0 0 0 4px ${dotColor}22` : 'none',
      ...(current
        ? ({
          '--tl-pulse-color': `${gl}99`,
          '--tl-glow-color': `${gl}22`,
        } as React.CSSProperties)
        : {}),
    };

    return (
      <div className={classNames('relative flex gap-4', className)}>
        {/* Connector line */}
        {!last && (
          <div
            className="absolute left-[18px] top-9 bottom-0 w-px"
            style={{ background: connectorColor }}
          />
        )}
        {/* Dot */}
        <div className="shrink-0 flex flex-col items-center">
          <div
            className={classNames(
              `w-9 h-9 rounded-full flex items-center justify-center ring-[3px] ${APP_BORDER.timelineDotRing}`,
              current && 'tl-dot-current',
            )}
            style={dotStyle}
          >
            <span className={done ? 'text-white' : 'opacity-30'}>{icon}</span>
          </div>
        </div>
        {/* Content */}
        <div className={classNames('pb-5 flex-1', !done && 'opacity-35')}>
          <p
            className={`text-[12px] font-semibold leading-none mb-1 ${APP_TEXT.default}`}
          >
            {effectiveTitle}
          </p>
          {value ? (
            <>
              <p className={`text-[11px] ${APP_TEXT.soft}`}>
                {getDateTime(value)}
              </p>
              <p
                className={`text-[10px] mt-0.5 tabular-nums ${APP_TEXT.subtle}`}
              >
                {getDateTimeAgo(value)}
              </p>
            </>
          ) : (
            <p className={`text-[11px] italic ${APP_TEXT.subtle}`}>
              Not yet reached
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── ③ Default mode ──────────────────────────────────────────────────────
  const inner = (
    <div
      className={classNames(
        'relative pl-9 pb-5 last:pb-0 cursor-default group',
        className,
      )}
    >
      {/* Vertical connector */}
      <div className="absolute left-[11px] top-7 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700/50 group-last:hidden" />
      {/* Dot */}
      <div
        className="absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
        style={{ background: dotColor }}
      >
        {icon}
      </div>
      {/* Content */}
      <Text as="p" size="base" weight="bold" color="default" leading="snug">
        {effectiveTitle}
      </Text>
      {subtitle && (
        <Text as="p" size="md" color="muted">
          {subtitle}
        </Text>
      )}
      {meta && (
        <Text as="p" size="sm" color="subtle" className="tabular-nums">
          {meta}
        </Text>
      )}
    </div>
  );

  if (!tooltip) return inner;
  return (
    <Tooltip content={tooltip} placement="top">
      {inner}
    </Tooltip>
  );
};
